// src/services/pftExport.js
//
// "Export" on the PFT Uploads page (admins, with PFT filters on): the filtered cards as
// a CSV that opens in Excel. Columns decided 2026-10-19: Name, DOB, MRN, the PFT date,
// and only the values the filter rules use, from each card's NEWEST PFT (the same test
// the filter checks). Firebase-free and tested (pftExport.test.js); the page records
// the export in the audit trail before downloading.

import { filterFieldLabel, isActiveRule, newestTestOf } from "./pftCardFilter";
import { softChecksOf } from "./pftSoftChecks";

/** Each filtered field once, in rule order. */
export const exportFields = (rules = []) => [...new Set(rules.filter(isActiveRule).map((r) => r.field))];

// 2026-10-27: an exported value only ONE extraction reading found (and nobody verified)
// may have decided the filter, so the export says so. A CSV can't outline a cell, so one
// extra last column names those values; it is added only when some row has one, so other
// exports are exactly as before.
export const RECHECK_HEADER = "Recheck against the PDF (only one reading found these values)";

const recheckOf = (card, t, fields) =>
  softChecksOf(t, { patientAddress: card.address, patientDob: card.dob })
    .filter((s) => s.oneReading && !s.verified && fields.includes(s.key))
    .map((s) => filterFieldLabel(s.key));

/** [header, ...rows] for the given cards. */
export const filteredExportRows = (cards = [], rules = []) => {
  const fields = exportFields(rules);
  const header = ["Name", "DOB", "MRN", "PFT date", ...fields.map(filterFieldLabel)];
  const recheck = [];
  const rows = cards.map((c) => {
    const t = newestTestOf(c) || {};
    recheck.push(recheckOf(c, t, fields).join("; "));
    return [c.name, c.dob, c.mrn9 || c.mrnRaw, t.date, ...fields.map((k) => t[k])];
  });
  if (!recheck.some(Boolean)) return [header, ...rows];
  return [[...header, RECHECK_HEADER], ...rows.map((r, i) => [...r, recheck[i]])];
};

// A cell starting with = + - @ would run as a formula in Excel: prefix it with '.
// Plain numbers (a negative reversibility, -3.5) stay numbers.
const cell = (v) => {
  const s = v === undefined || v === null ? "" : String(v);
  const safe = /^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s) ? `'${s}` : s;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export const toCsv = (rows) => rows.map((r) => r.map(cell).join(",")).join("\r\n") + "\r\n";

/** File name: the site and the date only, never a patient's name. */
export const exportFileName = (siteId, now = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  const day = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return `pft-filtered_${String(siteId || "site").replace(/[^\w-]/g, "")}_${day}.csv`;
};

/** Download in the browser. The BOM makes Excel read the file as UTF-8. */
export const downloadCsv = (fileName, csv) => {
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
